import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AlignLeft, Search, Heart, Handbag, CircleUserRound, X, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { productService } from '../../services/productService';
import MobileDrawer from '../MobileDrawer/MobileDrawer';
import MenuLink from '../MenuLink/MenuLink';
import { useSiteLayout } from '../../context/SiteLayoutContext';
import './Header.css';

const DEFAULT_LOGO = '/images/sha-botique-logo.png';

const Header = () => {
  // Logo, menu, colours and search text come from Admin → Customize → Header
  const { layout } = useSiteLayout();
  const { user } = useAuth();
  const headerContent = layout?.header;
  const navLinks = headerContent?.navLinks || [];
  const trendingTags = (headerContent?.trendingSearches || []).map(t => t.text);
  const logo = headerContent?.logo || DEFAULT_LOGO;
  const logoAlt = headerContent?.logoAlt || 'ZIDA';
  const headerStyle = headerContent
    ? {
        '--color-bg': headerContent.bgColor,
        '--color-text': headerContent.textColor,
        '--color-primary': headerContent.accentColor,
        '--logo-height': `${headerContent.logoHeight}px`
      }
    : undefined;

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [quickResults, setQuickResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Close search and drawer on location change
  useEffect(() => {
    setIsMobileDrawerOpen(false);
    setIsSearchOpen(false);
    setShowSuggestions(false);
  }, [location.pathname, location.search]);

  // Handle scroll shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus search input when overlay opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Live quick search preview
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length >= 2) {
      productService.getProducts({ searchQuery: trimmed })
        .then(res => setQuickResults(res.slice(0, 4)))
        .catch(() => setQuickResults([]));
    } else {
      setQuickResults([]);
    }
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setShowSuggestions(false);
    }
  };

  const handleTagClick = (tag) => {
    setSearchQuery(tag);
    navigate(`/search?q=${encodeURIComponent(tag)}`);
    setIsSearchOpen(false);
    setShowSuggestions(false);
  };

  return (
    <>
      <header className={`site-header ${isScrolled ? 'scrolled' : ''}`} style={headerStyle}>
        <div className="header-container">
          {/* Mobile Left: Menu Hamburger & Mobile Search */}
          <div className="header-left mobile-only">
            <button
              className="icon-button"
              aria-label="Open navigation menu"
              onClick={() => setIsMobileDrawerOpen(true)}
            >
              <AlignLeft size={22} />
            </button>
            <button
              className="icon-button"
              aria-label="Search collection"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search size={20} />
            </button>
          </div>

          {/* Desktop Left / Center: Brand Logo */}
          <div className="header-logo-section">
            <Link to="/" className="brand-logo" aria-label={`${logoAlt} Home`}>
              <img
                src={logo}
                alt={logoAlt}
                className="brand-logo-img"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-navigation" aria-label="Main Navigation">
            <ul className="nav-list">
              {navLinks.map((link) => (
                <li key={link.id} className="nav-item">
                  <MenuLink
                    link={link}
                    className={isActive => `nav-link ${isActive ? 'active' : ''} ${link.highlight ? 'nav-link-highlight' : ''}`}
                  >
                    {link.label}
                  </MenuLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Header Right: Search, Account, Wishlist, Cart */}
          <div className="header-actions">
            {/* Desktop Quick Search Form */}
            <div className="desktop-search-wrapper desktop-only" ref={searchContainerRef}>
              <form onSubmit={handleSearchSubmit} className="desktop-search-form">
                <input
                  type="text"
                  placeholder={headerContent?.searchPlaceholder || 'Search products...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  className="desktop-search-input"
                  aria-label="Search products"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setQuickResults([]); }}
                    className="desktop-search-clear"
                    aria-label="Clear search input"
                  >
                    <X size={14} />
                  </button>
                )}
                <button type="submit" className="search-submit-btn" aria-label="Submit search">
                  <Search size={17} />
                </button>
              </form>

              {/* Desktop Live Suggestions Dropdown */}
              {showSuggestions && (
                <div className="desktop-search-dropdown">
                  {quickResults.length > 0 ? (
                    <div className="search-dropdown-results">
                      <span className="dropdown-section-label">Products</span>
                      {quickResults.map(p => (
                        <Link
                          key={p.id}
                          to={`/products/${p.slug}`}
                          className="search-dropdown-item"
                          onClick={() => setShowSuggestions(false)}
                        >
                          <img src={p.images[0]} alt={p.name} className="dropdown-item-thumb" />
                          <div className="dropdown-item-info">
                            <span className="dropdown-item-name">{p.name}</span>
                            <span className="dropdown-item-price">₹{p.price}</span>
                          </div>
                        </Link>
                      ))}
                      <button
                        type="button"
                        className="search-dropdown-viewall"
                        onClick={handleSearchSubmit}
                      >
                        View all results for "{searchQuery}" <ArrowRight size={14} />
                      </button>
                    </div>
                  ) : searchQuery.trim().length >= 2 ? (
                    <div className="search-dropdown-noresults">
                      <span>No matching styles found</span>
                    </div>
                  ) : null}

                  {trendingTags.length > 0 && (
                  <div className="search-dropdown-trending">
                    <span className="dropdown-section-label">Trending Searches</span>
                    <div className="dropdown-tag-pills">
                      {trendingTags.map(tag => (
                        <button
                          key={tag}
                          type="button"
                          className="dropdown-tag-btn"
                          onClick={() => handleTagClick(tag)}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>

            {/* Account Icon */}
            <Link to="/account" className="icon-button desktop-only" aria-label={user ? `My Account (${user.name || 'logged in'})` : 'Log in or sign up'} title={user ? (user.name ? `Hi, ${user.name.split(' ')[0]}` : 'My Account') : 'Log in / Sign up'}>
              <CircleUserRound size={22} strokeWidth={1.75} />
              {user && <span className="account-dot" aria-hidden="true" />}
            </Link>

            {/* Wishlist Icon with Counter */}
            <Link to="/wishlist" className="icon-button" aria-label={`Wishlist, ${wishlistCount} items`}>
              <Heart size={21} strokeWidth={1.75} />
              {wishlistCount > 0 && <span className="action-badge">{wishlistCount}</span>}
            </Link>

            {/* Cart Bag with Counter */}
            <Link to="/cart" className="icon-button cart-icon-btn" aria-label={`Shopping Bag, ${cartCount} items`}>
              <Handbag size={22} strokeWidth={1.75} />
              {cartCount > 0 && <span className="action-badge bag-badge">{cartCount}</span>}
            </Link>
          </div>
        </div>

        {/* Expandable Search Overlay for Mobile */}
        {isSearchOpen && (
          <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search Site">
            <div className="search-overlay-inner">
              <form onSubmit={handleSearchSubmit} className="search-overlay-form">
                <Search size={22} className="search-overlay-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by kurta, anarkali, cotton set, colour, size..."
                  className="search-overlay-input"
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="clear-search-btn" aria-label="Clear search input">
                    <X size={18} />
                  </button>
                )}
                <button type="submit" className="search-go-btn">Search</button>
              </form>
              <button
                className="close-search-btn"
                onClick={() => setIsSearchOpen(false)}
                aria-label="Close search overlay"
              >
                <X size={24} />
              </button>
            </div>
            {trendingTags.length > 0 && (
            <div className="search-quick-tags">
              <span className="quick-tag-label">Popular:</span>
              {trendingTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
            )}
          </div>
        )}
      </header>

      {/* Mobile Drawer — uses the same menu links, logo and colours as the header */}
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        navLinks={navLinks}
        logo={logo}
        logoAlt={logoAlt}
        promoText={headerContent?.mobilePromoText}
        promoCode={headerContent?.mobilePromoCode}
        themeStyle={headerContent ? {
          '--drawer-head-bg': headerContent.bgColor,
          '--drawer-head-text': headerContent.textColor
        } : undefined}
      />
    </>
  );
};

export default Header;
